import { Injectable } from '@angular/core';
import { AppFeature } from '@app/core/models/config.models';
import { ApiService } from '@app/core/services/api.service';
import { AppConfigService } from '@app/core/services/app-config.service';
import { WebSocketService } from '@app/core/services/websocket.service';
import { ElectronService } from '@app/electron/electron.service';
import { Call } from '@app/phone/models/call.model';
import { CompanionCall } from '@app/phone/models/companion.models';
import { CompanionService } from '@app/phone/services/companion.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IntegrationsAdapter } from '@shared/adapters/integrations-adapter';
import { CallDirection } from '@shared/models/call-direction.models';
import { CallEventTypes } from '@shared/models/call-events.models';
import { Integration, WebPopCallObject } from '@shared/models/integrations.models';
import { SessionStatus } from '@shared/session-status.model';
import { ElectronChannel, ElectronInvocationChannel } from '@shared/types';
import {
  createIntegrationUrl,
  createWebPopCallObj,
  EventCallIdKey,
  handleCallAnsweredEvent,
  handleCallRangEvent,
  handleCallStartedEvent,
  WebPopEventContext,
} from '@shared/utils/integrations.utils';
import { BehaviorSubject, finalize, firstValueFrom, map, Observable, of, tap } from 'rxjs';

import { LambdaService } from './lambda.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class RendererIntegrationsService extends ApiService implements IntegrationsAdapter {
  private readonly integrationsSubject = new BehaviorSubject<Integration[] | undefined>(undefined);
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly handledEventCallIds = new Set<EventCallIdKey>();
  private webPopListenersActive = false;

  /**
   * The list of available integrations. Will be undefined until the first time the API call
   * has been made. At that point it will be an empty array if no integrations are available.
   */
  public integrations$ = this.integrationsSubject.asObservable();
  public nonAutoWebPopIntegrations$ = this.integrations$.pipe(
    map((integrations) => this.getNonAutoWebPopIntegrations(integrations ?? []))
  );
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private lambdaService: LambdaService,
    private appConfigService: AppConfigService,
    private electronService: ElectronService,
    private companionService: CompanionService,
    private webSocketService: WebSocketService
  ) {
    super();
  }

  public init() {
    // In Electron mode, set up companion state sync to main process
    // Main process handles all WebSocket event processing
    if (this.electronService.isRunningInElectron) {
      this.setupCompanionStateSync();
    }

    // Track feature flag state and set up browser auto-pop when enabled
    this.appConfigService.features$.pipe(untilDestroyed(this)).subscribe((features) => {
      if (features[AppFeature.WebPopIntegration]) {
        if (this.integrationsSubject.value === undefined) {
          // Data will be fetched via refreshData() in the initialization flow
        }
        if (!this.electronService.isRunningInElectron) {
          this.setUpWebPopListeners();
        }
      }
    });
  }

  private setUpWebPopListeners(): void {
    if (this.webPopListenersActive) return;
    this.webPopListenersActive = true;

    const ctx: WebPopEventContext = {
      handledEventCallIds: this.handledEventCallIds,
      adapter: this,
      getProvision: () => this.appConfigService.provision?.data,
      getWebpopSettings: () => this.appConfigService.webpopSettings ?? undefined,
    };

    const onError = (eventName: string, err: unknown) =>
      console.error(`[RENDERER-INTEGRATIONS] ${eventName} handler error:`, err);

    this.webSocketService.on(CallEventTypes.CallRang, (event) =>
      handleCallRangEvent(event, ctx).catch((err) => onError('CallRang', err))
    );
    this.webSocketService.on(CallEventTypes.CallAnswered, (event) =>
      handleCallAnsweredEvent(event, ctx).catch((err) => onError('CallAnswered', err))
    );
    this.webSocketService.on(CallEventTypes.CallStarted, (event) =>
      handleCallStartedEvent(event, ctx).catch((err) => onError('CallStarted', err))
    );
  }

  /**
   * Syncs companion call state to main process so it can filter outgoing companion calls.
   */
  private setupCompanionStateSync(): void {
    // Subscribe to companion call status changes and notify main process
    this.companionService.callStatus$.pipe(untilDestroyed(this)).subscribe(({ call, status }) => {
      if (call.direction !== 'outgoing') return;

      if (status === SessionStatus.Hangup) {
        this.electronService.send(ElectronChannel.CompanionCallEnded, { callId: call.id });
      } else {
        // For any non-ended status (Connecting, Active, etc.), ensure main knows about the call
        this.electronService.send(ElectronChannel.CompanionCallStarted, { callId: call.id });
      }
    });
  }

  // ========== IntegrationsAdapter Implementation ==========

  public async fetchIntegrations(): Promise<Integration[]> {
    if (!this.appConfigService.features[AppFeature.WebPopIntegration]) {
      return [];
    }

    if (this.electronService.isRunningInElectron) {
      // Fetch integrations from main process (main is source of truth)
      const integrations = await this.electronService.invoke(
        ElectronInvocationChannel.IntegrationsRefreshData,
        undefined
      );
      this.integrationsSubject.next(integrations);
      return integrations;
    } else {
      // Browser mode: fetch directly
      return await firstValueFrom(this.refreshDataInternal());
    }
  }

  public async updateIntegration(
    integration: Pick<Integration, 'id' | 'enabled' | 'auto_pop' | 'auto_dial'>
  ): Promise<void> {
    if (this.electronService.isRunningInElectron) {
      // Send update to main process (main is source of truth)
      await this.electronService.invoke(ElectronInvocationChannel.IntegrationsUpdateIntegration, {
        integration,
      });
    } else {
      // Browser mode: make API call directly
      const body = {
        enabled: integration.enabled,
        auto_pop: integration.auto_pop,
        auto_dial: integration.auto_dial,
      };
      await firstValueFrom(this.put<void>(`users/{me}/link-integrations-extensions/${integration.id}`, body));
    }

    // Update local cache with the changed properties
    const integrations = this.integrationsSubject.value;
    if (integrations) {
      const updatedIntegrations = integrations.map((i) =>
        i.id === integration.id
          ? { ...i, enabled: integration.enabled, auto_pop: integration.auto_pop, auto_dial: integration.auto_dial }
          : i
      );
      this.integrationsSubject.next(updatedIntegrations);
    }
  }

  public getIntegrations(): Integration[] {
    return this.integrationsSubject.value ?? [];
  }

  public openUrl(url: string): void {
    if (this.electronService.isRunningInElectron) {
      this.electronService.send(ElectronChannel.OpenLink, { url });
    } else {
      window.open(url, '_blank');
    }
  }

  public async openWebPopForIntegration(
    integration: Integration,
    call: Call | CompanionCall | WebPopCallObject
  ): Promise<void> {
    let webPopCall: WebPopCallObject;
    if (call instanceof Call) {
      webPopCall = this.createWebPopCallWithSIPCall(call);
    } else if (call instanceof CompanionCall) {
      webPopCall = this.createWebPopCallWithCompanionCall(call);
    } else {
      webPopCall = call;
    }

    if (this.electronService.isRunningInElectron) {
      // Delegate to main process
      await this.electronService.invoke(ElectronInvocationChannel.IntegrationsOpenWebPop, {
        integration,
        callObj: webPopCall,
      });
    } else {
      // Browser mode: open directly
      const url = await this.getIntegrationUrl(integration, webPopCall);
      if (url) {
        this.openUrl(url);
      }
    }
  }

  public hasOutgoingCompanionCall(callId: string): boolean {
    return this.companionService.hasOutgoingCall(callId);
  }

  // ========== Backward Compatibility Methods ==========

  /**
   * @deprecated Use fetchIntegrations() instead. This method is kept for backward compatibility.
   */
  public refreshData(): Observable<Integration[] | void> {
    if (!this.electronService.isRunningInElectron) {
      if (!this.appConfigService.features[AppFeature.WebPopIntegration]) {
        return of(void 0);
      }
      return this.refreshDataInternal();
    }

    // In Electron mode, wrap the async call
    this.isLoadingSubject.next(true);
    this.fetchIntegrations()
      .then(() => this.isLoadingSubject.next(false))
      .catch(() => this.isLoadingSubject.next(false));

    return of(void 0);
  }

  /**
   * @deprecated Use updateIntegration() instead. This method is kept for backward compatibility.
   */
  public async putIntegration(
    integration: Pick<Integration, 'id' | 'enabled' | 'auto_pop' | 'auto_dial'>
  ): Promise<void> {
    return this.updateIntegration(integration);
  }

  // ========== Private Methods ==========

  private refreshDataInternal(): Observable<Integration[]> {
    this.isLoadingSubject.next(true);
    return this.get<Integration[]>(`users/{me}/link-integrations-extensions`).pipe(
      untilDestroyed(this),
      tap((integrations) => {
        this.integrationsSubject.next(integrations);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private async getIntegrationUrl(integration: Integration, callObject: WebPopCallObject): Promise<string | undefined> {
    // Assign the integration's customer_id value to the call object
    callObject.customer_id = integration.variables.customer_id ?? '';

    let url: string | undefined;
    if (integration.lambda) {
      const result = await this.lambdaService.callLambdaFunction(
        integration.display_name,
        integration.lambda,
        callObject
      );
      url = result.lambdaResult;
    } else {
      url = createIntegrationUrl(integration, callObject);
    }
    return url;
  }

  private createWebPopCallWithSIPCall(call: Call): WebPopCallObject {
    // Strip trailing alphabetic characters so we're left only with the leading numeric values
    const phoneNumber = call.session.remoteIdentity.uri.user?.replace(/[a-zA-Z]+$/, '') || '';
    const isIncoming = call.direction === 'incoming';

    return createWebPopCallObj({
      phoneNumber,
      callerId: isIncoming ? call.remoteUriUser : call.localUriUser,
      callerName: call.remoteDisplayName || call.remoteUriUser,
      dialedNumber: isIncoming ? (call.origDID ?? call.localUriUser) : (call.origDialNumber ?? call.remoteUriUser),
      callId: call.origCallId || call.id,
      callDirection: isIncoming ? CallDirection.incoming : CallDirection.outgoing,
      dateTime: call.createdTimestamp.toISOString(),
    });
  }

  private createWebPopCallWithCompanionCall(call: CompanionCall): WebPopCallObject {
    // Strip trailing alphabetic characters so we're left only with the leading numeric values
    const phoneNumber = call.remoteUriUser.replace(/[a-zA-Z]+$/, '');
    const isIncoming = call.direction === 'incoming';

    return createWebPopCallObj({
      phoneNumber,
      callerId: isIncoming ? call.remoteUriUser : call.localUriUser,
      callerName: call.remoteDisplayName || call.remoteUriUser,
      dialedNumber: isIncoming ? call.localUriUser : call.remoteUriUser,
      callId: call.id,
      callDirection: isIncoming ? CallDirection.incoming : CallDirection.outgoing,
      dateTime: call.createdTimestamp.toISOString(),
    });
  }

  private getNonAutoWebPopIntegrations(integrations: Integration[]): Integration[] {
    return integrations.filter(
      (integration) => integration.enabled && (!integration.auto_pop || !integration.auto_dial)
    );
  }
}
